
import { ComponentType } from 'react';
import {
  AwsIcon,
  BarChartIcon,
  ChecksIcon,
  ClipboardIcon,
  CloudIcon,
  DeviceImacIcon,
  HeartRateMonitorIcon,
  KeyIcon,
  PeopleIcon,
  PhoneIcon,
  PuzzleIcon,
  ShieldIcon,
  SpeedIcon,
} from '../theme/svgs';

/**
 * This file contains the navigation structure for external tools and services
 * used across the Chick-fil-A organization. These tools are displayed in the
 * sidebar for quick access.
 *
 * @remarks
 * This configuration follows Backstage patterns and uses the dropdownItems
 * prop pattern for scalable nested navigation.
 *
 * @see {@link https://backstage.io/docs/features/software-catalog/ | Backstage Software Catalog}
 */
/**
 * Represents a single link to an external tool or service
 */
export interface ExternalToolLink {
  readonly title: string;
  readonly to: string;
}

/**
 * Represents a category of external tools and services with nested links (subcategories) and an associated icon
 */
export interface ExternalToolCategory {
  readonly title: string;
  readonly icon: ComponentType<any>;
  readonly children: readonly ExternalToolLink[];
}

export const externalToolsNavigation: readonly ExternalToolCategory[] = [
    {
        title: 'AWS',
        icon: AwsIcon,
        children: [
            {
                title: 'Accounts list',
                to: 'https://homestead.app.cfahome.com/catalog?filters%5Bkind%5D=resource&filters%5Btype%5D=aws-account&filters%5Buser%5D=all',
            },
            {
                title: 'Accounts CI CMDB',
                to: 'https://cfaprod.service-now.com/now/nav/ui/classic/params/target/u_cmdb_ci_account_amazon_list.do%3Fsysparm_query%3D%26sysparm_first_row%3D1%26sysparm_view%3D',
            },
            {
                title: 'Billing',
                to: 'https://awsbilling.ittools.cfahome.com/tools',
            },
            {
                title: 'Console Login',
                to: 'https://signin.aws.amazon.com/saml',
            },
            {
                title: 'FinOps Dashboards',
                to: 'https://us-east-1.quicksight.aws.amazon.com/sn/start/dashboards',
            },
        ],
    },
    {
        title: 'CI/CD',
        icon: ChecksIcon,
        children: [
            {
                title: 'GitHub',
                to: 'https://github.com/',
            },
            {
                title: 'Docker',
                to: 'https://app.docker.com/sso/login?state=4MUmdYz3LsPMQKemrXVOFw',
            },
            {
                title: 'JFrog Artifactory',
                to: 'https://cfa.jfrog.io/ui/login',
            },
        ],
    },
    {
        title: 'Collab Tools',
        icon: PhoneIcon,
        children: [
            {
                title: 'Confluence',
                to: 'https://cfacorp.atlassian.net/wiki/home',
            },
            {
                title: 'Jira',
                to: 'https://cfacorp.atlassian.net/jira',
            },
            {
                title: 'Miro',
                to: 'https://miro.com/app/dashboard/',
            },
            {
                title: 'Service Gateway',
                to: 'https://cfahome.okta.com/home/bookmark/0oah8miemoLr92Ey21t7/2557',
            },
            {
                title: 'ServiceNow',
                to: 'https://cfaprod.service-now.com/now/nav/ui/home',
            },
            {
                title: 'Slack',
                to: 'https://cfahome.okta.com/home/slack/0oazhhzhcbL1wo17Y1t7/19411',
            },
            {
                title: 'Trello',
                to: 'https://trello.com/',
            },
        ],
    },
    {
        title: 'Cybersecurity',
        icon: ShieldIcon,
        children: [
            {
                title: 'Banyan',
                to: 'https://cfacorp.atlassian.net/wiki/spaces/TEAM/pages/4221603041/Banyan+Security+Documentation',
            },
            {
                title: 'Snyk',
                to: 'https://cfahome.okta.com/home/cfahome_snyk_1/0oakqfi5nv6b6VKd11t7/alnkqfot4oJDys3Em1t7',
            },
            {
                title: 'Wiz',
                to: 'https://cfahome.okta.com/home/bookmark/0oaj19xbj90nOqWPv1t7/2557',
            },
        ],
    },
    {
        title: 'Data',
        icon: BarChartIcon,
        children: [
            {
                title: 'Analytics Hub',
                to: 'https://analytics.cfahome.com/',
            },
            {
                title: 'Anomalo',
                to: 'https://cfahome.okta.com/home/anomalo/0oarmpib9xQjt0wTu1t7/aln1jimnqjzdEPDaG1d8',
            },
            {
                title: 'Data Exchange',
                to: 'https://portal.dataexchange.cfahome.com/',
            },
            {
                title: 'Tableau',
                to: 'https://cfahome.okta.com/home/chickfila_tableauserver_1/0oabfbhz4sUAfmwDd1t7/alnbfbm6f7oneES5t1t7',
            },
        ],
    },
    {
        title: 'Dev Tools',
        icon: DeviceImacIcon,
        children: [
            {
                title: 'Alohomora ("Aloha")',
                to: 'https://github.com/cfacorp/alohomora',
            },
            {
                title: 'CFA-API-SDK',
                to: 'https://docs.cfasdk.cfadevelop.com/',
            },
            {
                title: 'LaunchDarkly',
                to: 'https://example.com',
            },
            {
                title: 'Postman',
                to: 'https://chick-fil-a.postman.co/',
            },
        ],
    },
    {
        title: 'Identity',
        icon: KeyIcon,
        children: [
            {
                title: 'Chick-fi-Login utility',
                to: 'https://github.com/cfacorp/cfa-login/blob/master/readme.md',
            },
            {
                title: 'CyberArk',
                to: 'https://epv.cfahome.com/PasswordVault/v10/logon?returnUrl=%2FAccounts',
            },
            {
                title: 'IAM Tools',
                to: 'https://cfahome.okta.com/home/oidc_client/0oafrf2n44rxiXfbI1t7/alntwmdyyUB5fs8d50g4',
            },
            {
                title: 'LastPass',
                to: 'https://example.com',
            },
            {
                title: 'MFA Reset',
                to: 'https://profile.idm.cfahome.com/?action=testmfa',
            },
            {
                title: 'okta-aws-cli',
                to: 'https://cfacorp.atlassian.net/wiki/spaces/IDENTITY/pages/4460774843/Okta-aws-cli',
            },
            {
                title: 'Okta',
                to: 'https://cfahome.okta.com/app/UserHome?session_hint=AUTHENTICATED',
            },
            {
                title: 'SailPoint',
                to: 'https://cfa.identityiqcms.com/identityiq/home.jsf',
            },
        ],
    },
    {
        title: 'People',
        icon: PeopleIcon,
        children: [
            {
                title: 'DTT Connect',
                to: 'https://cfacorp.sharepoint.com/sites/DTTConnect/SitePages/ProjectHome.aspx',
            },
            {
                title: 'DTT TeamView',
                to: 'https://cfahome.okta.com/home/bookmark/0oa170odim6fqTHsg1t8/2557',
            },
            {
                title: 'Org Chart',
                to: 'https://org.stscore.cfahome.com/people/13360',
            },
            {
                title: 'Chikin Coop(Staff/EW)',
                to: 'https://cfa-staff-cw-dashboard.stsapi.cfahome.com/home',
            },
        ],
    },
    {
        title: 'Platforms',
        icon: CloudIcon,
        children: [
            {
                title: 'Cloudflare',
                to: 'https://cfahome.okta.com/home/oidc_client/0oavfo3hdh1iwIdgw1t7/alntwmdyyUB5fs8d50g4',
            },
            {
                title: 'Kube Self-Serve',
                to: 'https://kube-self-serve.prod-kube.cfahome.com/login',
            },
            {
                title: 'Kube Docs',
                to: 'https://kubedocs.cfahome.com/',
            },
            {
                title: 'Vercel',
                to: 'https://example.com',
            },
        ],
    },
    {
        title: 'Quality & R+M',
        icon: HeartRateMonitorIcon,
        children: [
            {
                title: 'Datadog - CTS',
                to: 'https://cfahome.okta.com/home/datadog/0oaa2xts4mP1nUrJd1t7/28057',
            },
            {
                title: 'Datadog - DE',
                to: 'https://cfahome.okta.com/home/cfahome_datadogde_1/0oakow5gpod4Y0vH01t7/alnkowcv3s2uw77Ra1t7',
            },
            {
                title: 'Datadog - DSP',
                to: 'https://cfahome.okta.com/home/cfahome_datadogdsp_1/0oakpyip63kog51fr1t7/alnkpyovl8uRRix5Y1t7',
            },
            {
                title: 'Datadog - STS|OTS',
                to: 'https://cfahome.okta.com/home/cfahome_datadogdsp_1/0oakpyip63kog51fr1t7/alnkpyovl8uRRix5Y1t7',
            },
            {
                title: 'Grafana',
                to: 'https://example.com',
            },
            {
                title: 'OpsGenie',
                to: 'https://cfahome.okta.com/home/OpsGenie/0oaa484fm3qCeUeUs1t7/aln19aa6xca4RRLUQ1d8',
            },
            {
                title: 'Rootly',
                to: 'https://cfahome.okta.com/home/cfahome_rootly_1/0oa17zype2qwIAqsY1t8/aln17zyrxpnKXlDfJ1t8',
            },
            {
                title: 'SonarQube',
                to: 'https://sonar.ittools.cfahome.com/',
            },
        ],
    },
    {
        title: 'Standards',
        icon: ClipboardIcon,
        children: [
            {
                title: 'Digital Playbook',
                to: 'https://ddc.ealabs.cfahome.com',
            },
            {
                title: 'Security Maturity Model',
                to: 'https://cfacorp.atlassian.net/wiki/spaces/TEAM/pages/4573102091/Secure+DevOps+Maturity+Model',
            },
        ],
    },
        {
        title: 'Status Pages',
        icon: SpeedIcon,
        children: [
            {
                title: 'AWS Status',
                to: 'https://health.aws.amazon.com/health/status',
            },
            {
                title: 'CFA Digital Performance Dashboard',
                to: 'https://cfahome.okta.com/home/cfahome_statuspagectscustomerexperience_1/0oavyc5oofApvWnZL1t7/alnvyc9snwQzrZpVD1t7',
            },
            {
                title: 'CFA Health Dashboard',
                to: 'https://status.cfahome.com/',
            },
            {
                title: 'GitHub Status',
                to: 'https://www.githubstatus.com/',
            },
            {
                title: 'JFrog Status',
                to: 'https://status.jfrog.io/',
            },
            {
                title: 'Slack Status',
                to: 'https://slack-status.com/',
            },
        ],
    },
    {
        title: 'Various Tools',
        icon: PuzzleIcon,
        children: [
            {
                title: 'Flaming Snakes',
                to: 'https://cfahome.okta.com/home/bookmark/0oa17eh426a7bjjhQ1t8/2557',
            },
            {
                title: 'LeanIX',
                to: 'https://cfacorp.leanix.net/cfa/',
            },
            {
                title: 'JetBrains',
                to: 'https://example.com',
            },
            {
                title: 'Redpanda',
                to: 'https://example.com',
            },
            {
                title: 'Sentry',
                to: 'https://example.com',
            },
            {
                title: 'Spacelift',
                to: 'https://example.com',
            },
            {
                title: 'Tango',
                to: 'https://tango.cfahome.com/',
            },
        ],
    },
];
